import { getNetworkStatus } from 'CHECKS/connectivity/nat';
import { navigatorDetails } from 'UTILS/navigator';
import { computeConnectionQuality, TOTAL_CONNECTION_QUALITY_DURATION } from 'CHECKS/connectivity/quality';
import { onUnhandledError } from 'UTILS/callbacks';

export const NAT_CHECK_MAX_DURATION = 2000; // ms
export const SAFARI_NAT_CHECK_MAX_DURATION = 10000; // ms
export const MAX_LATENCY = 500; // ms
export const CRITICAL_MAX_LATENCY = 1000; // ms
export const MAX_PACKET_LOSS_PERCENT = 18; // percent
export const CRITICAL_MAX_PACKET_LOSS_PERCENT = 36; // percent
export const MIN_QUALITY = 70; // percent
export const CRITICAL_MIN_QUALITY = 20; // percent

const start = async({
  onError = () => {},
  onWarning = () => {},
  onInfo = () => {},
  onSuccess = () => {},
  onProgress = () => {},
  onEnd = () => {},
  relayServers,
  logger,
}) => {
  let results = {};
  let interval;
  if (!navigator.onLine) {
    onError('no_network_connection');
    onEnd(results);
    return;
  }
  try {
    let progress = 10;
    onProgress(10);
    interval = setInterval(() => onProgress(progress += 5), TOTAL_CONNECTION_QUALITY_DURATION / 18);
    const { latency, packetLossPercent, bitrate, quality } = await computeConnectionQuality({
      relayServers,
      logger,
    });
    let warningCounter = 0;
    if (!isNaN(latency) && latency > MAX_LATENCY) {
      warningCounter++;
    }
    if (!isNaN(latency) && latency > CRITICAL_MAX_LATENCY) {
      warningCounter++;
    }
    if (!isNaN(packetLossPercent) && packetLossPercent > MAX_PACKET_LOSS_PERCENT) {
      warningCounter++;
    }
    if (!isNaN(packetLossPercent) && packetLossPercent > CRITICAL_MAX_PACKET_LOSS_PERCENT) {
      warningCounter++;
    }
    if (!isNaN(quality) && quality < MIN_QUALITY) {
      warningCounter++;
    }
    if (!isNaN(quality) && quality < CRITICAL_MIN_QUALITY) {
      warningCounter++;
    }
    if (warningCounter >= 2) {
      onWarning('very_bad_connection');
    } else if (warningCounter === 1) {
      onInfo('bad_connection');
    } else {
      onSuccess('good_connection');
    }
    const { nat, udp, relay } = await getNetworkStatus({
      iceConfig: {
        iceServers: relayServers,
        iceTransportPolicy: 'all',
      },
      candidatesTimeout: navigatorDetails.browser === 'safari' ? SAFARI_NAT_CHECK_MAX_DURATION : NAT_CHECK_MAX_DURATION,
      logger,
    });
    if (!udp) {
      onWarning('udp_not_supported');
    }
    switch (nat) {
      case 'both':
      case 'asymmetric':
        if (!relay) {
          onWarning('relay_access_impossible');
        }
        break;
      case 'noSrflx':
      case 'symmetric':
      default:
        if (relay) {
          onInfo('indirect_connection');
        } else {
          onError('indirect_connection_relay_access_impossible');
        }
        break;
    }
    results = {
      ...results,
      latency,
      packetLossPercent,
      bitrate,
      quality,
      nat,
      udp,
      relay,
    };
  } catch (error) {
    onError('connectivity_check_impossible', { error: error.message });
    onUnhandledError(error);
  } finally {
    clearInterval(interval);
    onEnd(results);
  }
};

export default {
  start,
};
